import { FC, useState, useEffect, useCallback } from 'react';
import * as Styled from './MemberProfile.styled';
import {
	handleInputsDisabled,
	createMemberProfileInputs,
	resetSuccessMessages,
	setMemberProfileInputs,
	validateEmail,
	validatePhoneNumber,
	validateName,
	hasFormErrors,
	getSubmitBody,
	setFavoriteLocationFromResponse,
	FavoriteLocations,
	setCookie,
	getCookie,
	removeCookie
} from './MemberProfileUtils';
import { useRouter } from 'next/navigation';
import { useSearchParams } from '../../../../../../shared/hooks/useSearchParams';
import { DineRewardsQuestionMark } from '@maverick/icons/dist/DineRewardsQuestionMark';
import { ReduxProps } from '.';
import { Input, InputModes } from '../../../../../ui/Input';
import { DineRewardsManager } from '../../../../../../shared/managers/DineRewards.manager';
import { Dropdown } from '../../../../../ui/Dropdown';
import { useGDPRExtensions } from '../../../../../../shared/hooks/useGDPRExtensions';
import { MemberProfileHeader } from './MemberProfileHeader';
import { Strings } from '../../../../../../shared/constants/Strings';
import { GaEvent } from '@maverick/utils';
import { useModal } from '../../../../../../shared/hooks/useModal';

interface MemberProfileProps extends ReduxProps { }

export const MemberProfile: FC<MemberProfileProps> = ({ userInfo, setUserInfo }) => {
	const { sectionTitles, formButtons } = Strings.memberProfile;
	const [savedChanges, setSavedChanges] = useState(false);
	const [selectedFavoriteLocation, setSelectedFavoriteLocation] = useState(FavoriteLocations.Default);
	const [printedCardNumber, setPrintedCardNumber] = useState<string>('');
	const router = useRouter();
	const gdprExtensions = useGDPRExtensions();
	const { searchParams } = useSearchParams();
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const { setModal } = useModal();

	const formInputs = createMemberProfileInputs();

	useEffect(() => {
		const retrieveUserData = async () => {
			if (userInfo?.mobileNum) {
				const response = await DineRewardsManager.GetAccountInfo(userInfo.mobileNum);
				if (response) {
					setMemberProfileInputs(response, formInputs);
					setSelectedFavoriteLocation(setFavoriteLocationFromResponse(response.favoriteLocation));
					setPrintedCardNumber(response.printedCardNumber);
				}
			}
		};
		retrieveUserData();
	}, [userInfo]);

	useEffect(() => {
		const isEdit = searchParams?.get('edit');
		if (isEdit === 'true') {
			setSavedChanges(false);
			setIsEditing(true);
			handleInputsDisabled(formInputs, false);
			GaEvent.StartProfileEdit();
			window.history.replaceState({}, '', '/dashboard/member-profile');
		}
	}, [searchParams]);

	const onClickCloseButton = () => {
		window.location.hash = '#available-rewards';
		window.dispatchEvent(new HashChangeEvent('hashchange'));
		router.push('/dashboard/');
	};

	const onClickEditButton = () => {
		router.push('/api/auth/mfa');
	};

	const handleChangeFavoriteLocation = (selected: string) => {
		setSelectedFavoriteLocation(selected);
		formInputs.phoneNumber.setSuccess(Strings.success.greatLetsContinue);
	};

	const validateForm = async () => {
		const promises = [];
		promises.push(validatePhoneNumber(formInputs.phoneNumber, userInfo));
		promises.push(validateEmail(formInputs.email, userInfo, gdprExtensions));
		promises.push(validateName(formInputs.firstName, formInputs.lastName, userInfo));
		const promisesResults = await Promise.all(promises);
		const promiseErrors = promisesResults.filter((result) => !result);
		if (promiseErrors.length > 0) {
			return false;
		}
		return true;
	};

	useEffect(() => {
		const newPassword = formInputs.newPassword;
		const confirmNewPassword = formInputs.confirmNewPassword;
		if (!newPassword.value && !confirmNewPassword.value) {
			formInputs.confirmNewPassword.setSuccess('');
		} else if (newPassword.value !== confirmNewPassword.value) {
			confirmNewPassword.setError(Strings.errors.password.doesNotMatch);
		} else {
			confirmNewPassword.setSuccess(Strings.success.greatLetsContinue);
		}
	}, [formInputs.newPassword.value, formInputs.confirmNewPassword.value]);

	const handleSignIn = useCallback(() => {
		window.location.href = `/api/auth/logout`;
	}, []);
	
	const handleSensitiveInfoChanged = () => {
		setCookie('userInfoChanged', 'true');
		setModal({
			showCloseButton: false,
			title: 'Your Dine Rewards account information was changed',
			outsideClick() {
				router.push('/api/auth/logout');
			},
			htmlString: 'Please sign-in using your updated credentials to access the latest on your rewards status, points balance, and more.',
			primaryButton: {
				label: 'Sign In',
				onClick: handleSignIn,
			},
			id: 'modal-logout',
		});
	}

	const handleOnSubmit = async () => {
		if (hasFormErrors(formInputs)) {
			return;
		}

		const isFormValid = await validateForm();
		if (!isFormValid) {
			GaEvent.ProfileEditFailure();
			return;
		}

		setIsEditing(false);
		handleInputsDisabled(formInputs, true);

		const body = getSubmitBody(formInputs, printedCardNumber, selectedFavoriteLocation);
		const { error, status } = await DineRewardsManager.PostEditAccountInfo(body);

		if (error) {
			setModal({
				id: Strings.modalError.id,
				title: Strings.modalError.title,
				status: status,
				htmlString: Strings.modalError.htmlString,
				primaryButton: {
					label: 'Email us',
					onClick: () => (window.location.href = `mailto:dinerewards@bloominbrands.com`),
				},
			});
			return;
		}

		const changedEmail = userInfo!.email !== body.email;
		const changedPhoneNumber = userInfo!.mobileNum !== body.phone;
		const changedPassword = !!body.password;

		setUserInfo({
			...userInfo,
			email: body.email,
			given_name: body.firstName,
			family_name: body.lastName,
			zip_code: body.postalCode,
			mobileNum: body.phone,
			date_of_birth: body?.dateOfBirth ?? null,
			sid: userInfo?.sid ?? '',
			loyaltyId: userInfo?.loyaltyId ?? '',
			origin: userInfo?.origin ?? '',
		});

		setSavedChanges(true);
		GaEvent.ProfileEditSuccess();

		resetSuccessMessages(formInputs);

		if (changedEmail || changedPhoneNumber || changedPassword) {
			handleSensitiveInfoChanged()
		}
	};

	useEffect(() => {
		const userInfoChanged = getCookie('userInfoChanged');
		if (userInfoChanged === 'true') {
			removeCookie('userInfoChanged');
			router.push('/api/auth/logout');
		}
	}, []);


	const renderPasswordRequirements = () => {
		const { newPassword } = formInputs;
		const { rules } = Strings.errors.password;
		if (!!newPassword.success) {
			return <Styled.ErrorMessageContainer>{newPassword.success}</Styled.ErrorMessageContainer>;
		}
		if (!newPassword.error) {
			return null;
		}
		return (
			<Styled.PasswordRules>
				<Styled.PasswordRuleRow>{rules[0]}</Styled.PasswordRuleRow>
				{rules.slice(1).map((rule, index) => (
					<Styled.PasswordRuleRow style={{ marginLeft: index < 2 ? '8px' : '20px' }} key={index}>
						{rule}
					</Styled.PasswordRuleRow>
				))}
			</Styled.PasswordRules>
		);
	};

	const renderAccountInfo = () => {
		return (
			<Styled.SectionInfo data-testid={'account-info-section'}>
				<Styled.SectionTitle tabIndex={0}>{sectionTitles.accountInfo}</Styled.SectionTitle>
				<Styled.InputContainer>
					<Input handler={formInputs.phoneNumber} mode={InputModes.Tel} />
					<Styled.ErrorMessageContainer>
						{formInputs.phoneNumber.success || formInputs.phoneNumber.error}
					</Styled.ErrorMessageContainer>
				</Styled.InputContainer>
				<Styled.InputContainer>
					<Input handler={formInputs.email} mode={InputModes.Email} />
					<Styled.ErrorMessageContainer>
						{formInputs.email.success || formInputs.email.error}
					</Styled.ErrorMessageContainer>
				</Styled.InputContainer>
			</Styled.SectionInfo>
		);
	};

	const renderSetPassword = () => {
		return (
			<Styled.SectionInfo data-testid={'set-password-section'}>
				<Styled.SectionTitle tabIndex={0}>{sectionTitles.setPassword}</Styled.SectionTitle>
				<Styled.InputContainer>
					<Input handler={formInputs.newPassword} mode={InputModes.Password} />
					{renderPasswordRequirements()}
				</Styled.InputContainer>
				<Styled.InputContainer>
					<Input handler={formInputs.confirmNewPassword} mode={InputModes.ConfirmPassword} />
					<Styled.ErrorMessageContainer>
						{formInputs.confirmNewPassword.success || formInputs.confirmNewPassword.error}
					</Styled.ErrorMessageContainer>
				</Styled.InputContainer>
			</Styled.SectionInfo>
		);
	};

	const renderPersonalInfo = () => {
		return (
			<Styled.SectionInfo data-testid='personal-info-section'>
				<Styled.SectionTitle tabIndex={0}>{sectionTitles.personalInfo}</Styled.SectionTitle>
				<Styled.NameContainer>
					<Styled.NameInput>
						<Styled.InputContainer>
							<Input handler={formInputs.firstName} mode={InputModes.Name} />
						</Styled.InputContainer>
						<Styled.InputContainer>
							<Input handler={formInputs.lastName} mode={InputModes.Name} />
						</Styled.InputContainer>
					</Styled.NameInput>
					<Styled.ErrorMessageContainer field='name'>
						{formInputs.firstName.error || formInputs.lastName.error || formInputs.lastName.success}
					</Styled.ErrorMessageContainer>
				</Styled.NameContainer>
				<Styled.InputContainer>
					<Input
						handler={formInputs.zipCode}
						mode={InputModes.ZipCode}
						icon={DineRewardsQuestionMark}
						tooltip={Strings.tooltips.zipCode}
					/>
					<Styled.ErrorMessageContainer>
						{formInputs.zipCode.success || formInputs.zipCode.error}
					</Styled.ErrorMessageContainer>
				</Styled.InputContainer>
			</Styled.SectionInfo>
		);
	};

	const renderExtraInfo = () => {
		return (
			<Styled.SectionInfo data-testid='extra-info-section'>
				<Styled.SectionTitle tabIndex={0}>{sectionTitles.enterYourBirthday}</Styled.SectionTitle>
				<Styled.InputContainer>
					<Input
						handler={formInputs.birthDate}
						mode={InputModes.BirthDate}
						icon={DineRewardsQuestionMark}
						tooltip={Strings.tooltips.birthday}
					/>
					<Styled.ErrorMessageContainer>
						{formInputs.birthDate.success || formInputs.birthDate.error}
					</Styled.ErrorMessageContainer>
				</Styled.InputContainer>
				<Styled.InputContainer>
					<Styled.DropdownLabel tabIndex={0}>{'Tell us where do you love to dine'}</Styled.DropdownLabel>
					<Dropdown
						data-testid='dropdown-input'
						disabled={!isEditing}
						items={Object.values(FavoriteLocations)}
						selected={selectedFavoriteLocation}
						onClickOption={handleChangeFavoriteLocation}
					/>
				</Styled.InputContainer>
			</Styled.SectionInfo>
		);
	};

	const renderContent = () => {
		return (
			<Styled.Content data-testid='profile-info'>
				{renderAccountInfo()}
				{renderSetPassword()}
				{renderPersonalInfo()}
				{renderExtraInfo()}

				{isEditing && (
					<Styled.FormButtonContainer>
						<Styled.SaveButton tabIndex={0} aria-label='save button' data-testid='save-button' onClick={handleOnSubmit}>
							{formButtons.save}
						</Styled.SaveButton>
						<Styled.ExitButton tabIndex={0} aria-label='exit button' data-testid='exit-button' onClick={onClickCloseButton}>
							{formButtons.exit}
						</Styled.ExitButton>
					</Styled.FormButtonContainer>
				)}
				{savedChanges && !isEditing && (
					<Styled.SavedChangesMessage>{Strings.success.changesSaved}</Styled.SavedChangesMessage>
				)}
			</Styled.Content>
		);
	};

	return (
		<Styled.Container data-testid='member-profile-container'>
			<MemberProfileHeader
				onClickCloseButton={onClickCloseButton}
				onClickEditButton={onClickEditButton}
				isEditing={isEditing}
			/>
			{renderContent()}
		</Styled.Container>
	);
};
